/**
 * gift-auto-add.js  —  プレゼント自動追加（独立モジュール）
 *
 * 機能:
 *   - 対象コレクションの商品がカートに存在する場合、プレゼント商品を 1 個だけ自動追加。
 *   - 対象商品がカートから無くなったら、プレゼントを自動削除。
 *   - プレゼント行は「プレゼント」ラベル表示＋数量変更不可＋削除不可（DOM 操作のみ）。
 *   - B2B（除外顧客タグ保有）は対象外（判定は gift-auto-add-config.liquid に集約）。
 *
 * 完全停止方法:
 *   方法1) テーマ設定「プレゼント機能を有効化」を OFF（このファイルは残してよい）。
 *   方法2) layout/theme.liquid から本ファイルの <script> と
 *          {% render 'gift-auto-add-config' %} の 2 行を削除。
 *   ※ 有料ラッピングの購入前確認も本ファイルを使うため、併用時は方法1で停止する。
 *
 * 既存ファイル（cart.js / product-form.js / cart-notification.js / 各カート liquid）は
 * 一切変更していない。表示・ロックはすべて本ファイルの DOM 操作で行う。
 *
 * 依存（すべて Dawn 標準）:
 *   - window.giftAutoAddConfig  … gift-auto-add-config.liquid が出力
 *   - window.routes.cart_add_url / cart_change_url … theme.liquid が出力
 *   - subscribe / publish / PUB_SUB_EVENTS … pubsub.js / constants.js（global）
 */
(function () {
  'use strict';

  var cfg = window.giftAutoAddConfig;
  if (!cfg) return;

  var GIFT_SOURCE = 'gift-auto-add';
  var giftVariantId = Number(cfg.giftVariantId) || 0;
  var targetIds = (cfg.targetProductIds || []).map(Number);
  var giftProp = cfg.giftProperty || '_auto_gift';

  var inFlight = false;
  var inFlightPromise = null;
  var pending = false;
  var checkoutInProgress = false;
  var lastCheckoutSubmitter = null;
  var lastKnownCart = null;

  // ---- 対象コレクションが 50 商品を超える場合の警告 ----
  if (typeof cfg.targetCount === 'number' && cfg.targetCount > cfg.targetLimit) {
    console.warn(
      '[Gift Auto Add]\n' +
        'Target collection contains ' + cfg.targetCount + ' products.\n' +
        'Current implementation supports up to ' + cfg.targetLimit + ' products.\n' +
        'Please switch to server-side matching strategy.'
    );
  }

  function noop() {}

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function makeError(action, status, data) {
    var error = new Error(
      (data && (data.description || data.message || data.errors)) || 'Gift auto add request failed.'
    );
    error.name = 'GiftAutoAddError';
    error.action = action;
    error.status = status || (data && data.status) || 0;
    error.data = data || null;
    return error;
  }

  function reportIssue(code, detail) {
    var payload = {
      code: code,
      detail: detail || null,
    };
    console.warn('[Gift Auto Add]', code, detail || '');
    try {
      window.dispatchEvent(new CustomEvent('gift-auto-add:error', { detail: payload }));
    } catch (e) {
      /* noop */
    }
  }

  function postJSON(url, payload, action) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    }).then(function (r) {
      return r
        .json()
        .catch(function () {
          return {};
        })
        .then(function (data) {
          if (!r.ok || (data && data.status >= 400) || (data && data.errors)) {
            throw makeError(action, r.status, data);
          }
          return data;
        });
    });
  }

  function getCart() {
    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      return r
        .json()
        .catch(function () {
          return {};
        })
        .then(function (data) {
          if (!r.ok || !data || !Array.isArray(data.items)) {
            throw makeError('get-cart', r.status, data);
          }
          return data;
        });
    });
  }

  function isCartPayload(data) {
    return !!(
      data &&
      Array.isArray(data.items) &&
      typeof data.item_count === 'number' &&
      Object.prototype.hasOwnProperty.call(data, 'total_price')
    );
  }

  function rememberCart(cart) {
    if (isCartPayload(cart)) lastKnownCart = cart;
    return cart;
  }

  function getCartForReconcile(options) {
    if (options && isCartPayload(options.cart)) return Promise.resolve(rememberCart(options.cart));
    return getCart().then(rememberCart);
  }

  // ---- 判定ロジック ----
  function isB2BExcluded() {
    return cfg.excludeB2B === true && cfg.isB2B === true;
  }

  function isGiftActive() {
    return cfg.enabled && !isB2BExcluded() && !!giftVariantId;
  }

  // カート判定は line item property のみで識別する（誤判定・無限ループ防止）。
  // /cart.js には必ず properties が含まれるため variant-ID フォールバックは使わない。
  // ※ variant-ID 照合は DOM ロック/ラベル表示（applyDomControls）専用とする。
  function isGiftLine(item) {
    return !!(item && item.properties && item.properties[giftProp]);
  }

  function cartHasTarget(cart) {
    return cart.items.some(function (item) {
      return !isGiftLine(item) && targetIds.indexOf(Number(item.product_id)) !== -1;
    });
  }

  function findGiftLine(cart) {
    return cart && cart.items ? cart.items.filter(isGiftLine)[0] : null;
  }

  function amount(value) {
    var n = Number(value);
    return Number.isFinite(n) ? n : null;
  }

  function hasLineDiscount(item) {
    var totalDiscount = amount(item.total_discount);
    var originalLinePrice = amount(item.original_line_price);
    var finalLinePrice = amount(item.final_line_price);
    return (
      (totalDiscount !== null && totalDiscount > 0) ||
      (originalLinePrice !== null && finalLinePrice !== null && originalLinePrice > finalLinePrice) ||
      !!(item.discounts && item.discounts.length)
    );
  }

  function isGiftFree(item) {
    var finalLinePrice = amount(item.final_line_price);
    var finalPrice = amount(item.final_price);
    var originalLinePrice = amount(item.original_line_price);

    if (finalLinePrice !== null && finalLinePrice !== 0) return false;
    if (finalLinePrice === null && finalPrice !== 0) return false;

    // 通常価格がある商品は、BXGY等で実際に割引されて0円になっていることを確認する。
    if (originalLinePrice !== null && originalLinePrice > 0) {
      return hasLineDiscount(item);
    }

    return finalLinePrice === 0 || finalPrice === 0;
  }

  // ---- カート操作 ----
  function addGift() {
    var props = {};
    props[giftProp] = 'true';
    return postJSON(window.routes.cart_add_url, {
      items: [{ id: giftVariantId, quantity: 1, properties: props }],
      sections: 'cart-icon-bubble',
      sections_url: window.location.pathname,
    }, 'add-gift').then(updateIconBubble);
  }

  function changeGift(key, quantity) {
    return postJSON(window.routes.cart_change_url, {
      id: key,
      quantity: quantity,
      sections: 'cart-icon-bubble',
      sections_url: window.location.pathname,
    }, quantity === 0 ? 'remove-gift' : 'change-gift').then(updateIconBubble);
  }

  // ヘッダーのカート個数バッジを更新（cart-items が無いページ向け）
  function updateIconBubble(response) {
    try {
      var html = response && response.sections && response.sections['cart-icon-bubble'];
      if (!html) return response;
      var parsed = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
      var target = document.getElementById('cart-icon-bubble');
      if (parsed && target) target.innerHTML = parsed.innerHTML;
    } catch (e) {
      /* noop */
    }
    return response;
  }

  function refreshCartPageFooter() {
    var footer = document.getElementById('main-cart-footer');
    if (!footer) return Promise.resolve();

    var sectionId = footer.dataset && footer.dataset.id;
    if (!sectionId) return Promise.resolve();

    var cartUrl = (window.routes && window.routes.cart_url) || root() + 'cart';
    return fetch(cartUrl + '?section_id=' + sectionId)
      .then(function (response) {
        return response.text();
      })
      .then(function (responseText) {
        var html = new DOMParser().parseFromString(responseText, 'text/html');
        var sourceContents = html.querySelector('#main-cart-footer .js-contents') || html.querySelector('.js-contents');
        var targetContents = footer.querySelector('.js-contents');

        if (sourceContents && targetContents) targetContents.innerHTML = sourceContents.innerHTML;
      })
      .catch(noop);
  }

  // 既存の仕組み（cart.js 等）にカート表示を再描画させる。自分の source で無限ループを防ぐ。
  function refreshCartUI() {
    var updatePromise = Promise.resolve();
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      updatePromise = Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, { source: GIFT_SOURCE }) || null);
    }

    return Promise.all([updatePromise, refreshCartPageFooter()]).then(function (results) {
      return results[0];
    });
  }

  function allowCheckout(reason, detail) {
    return { ok: true, blockCheckout: false, reason: reason || '', detail: detail || null };
  }

  function blockCheckout(reason, detail) {
    return { ok: false, blockCheckout: true, reason: reason || 'checkout-blocked', detail: detail || null };
  }

  function removeGiftBeforeCheckout(giftLine, reason) {
    return changeGift(giftLine.key, 0)
      .then(refreshCartUI)
      .then(function () {
        reportIssue(reason || 'gift-removed-before-checkout', {
          key: giftLine.key,
          final_line_price: giftLine.final_line_price,
          original_line_price: giftLine.original_line_price,
        });
        return allowCheckout(reason || 'gift-removed-before-checkout');
      })
      .catch(function (error) {
        reportIssue('gift-remove-failed-before-checkout', error);
        return blockCheckout('gift-remove-failed-before-checkout', error);
      });
  }

  function verifyCheckoutCart(cart) {
    rememberCart(cart);
    updateGuardedCartUI(cart, false);

    var giftLine = findGiftLine(cart);
    var active = isGiftActive();
    var wantGift = active && cartHasTarget(cart);

    if (!giftLine) {
      return allowCheckout(wantGift ? 'gift-missing-checkout-allowed' : 'no-gift-needed');
    }

    if (!active || !wantGift) {
      return removeGiftBeforeCheckout(giftLine, 'gift-not-eligible-removed-before-checkout');
    }

    if (giftLine.quantity !== 1) {
      return changeGift(giftLine.key, 1)
        .then(refreshCartUI)
        .then(getCart)
        .then(verifyCheckoutCart)
        .catch(function (error) {
          reportIssue('gift-quantity-fix-failed-before-checkout', error);
          return blockCheckout('gift-quantity-fix-failed-before-checkout', error);
        });
    }

    if (!isGiftFree(giftLine)) {
      return removeGiftBeforeCheckout(giftLine, 'paid-gift-removed-before-checkout');
    }

    return allowCheckout('gift-free-verified');
  }

  // ---- 整合処理（追加 / 削除 / 数量1 強制） ----
  function reconcile(options) {
    options = options || {};
    var checkoutMode = options.mode === 'checkout';

    return getCartForReconcile(options)
      .then(function (cart) {
        updateGuardedCartUI(cart, false);

        var giftLine = findGiftLine(cart);

        // 機能 OFF または B2B 除外時 → プレゼントがあれば除去
        if (!isGiftActive()) {
          if (giftLine) {
            return changeGift(giftLine.key, 0).then(refreshCartUI).then(function () {
              return checkoutMode ? getCart().then(verifyCheckoutCart) : applyDomControls();
            });
          }
          return checkoutMode ? verifyCheckoutCart(cart) : applyDomControls(cart);
        }

        var wantGift = cartHasTarget(cart);

        if (wantGift && !giftLine) {
          return addGift()
            .then(refreshCartUI)
            .then(function () {
              return checkoutMode ? getCart().then(verifyCheckoutCart) : applyDomControls();
            })
            .catch(function (error) {
              reportIssue('gift-add-failed', error);
              return checkoutMode ? allowCheckout('gift-add-failed-checkout-allowed', error) : null;
            });
        }
        if (!wantGift && giftLine) {
          return changeGift(giftLine.key, 0).then(refreshCartUI).then(function () {
            return checkoutMode ? getCart().then(verifyCheckoutCart) : applyDomControls();
          });
        }
        if (wantGift && giftLine && giftLine.quantity !== 1) {
          return changeGift(giftLine.key, 1).then(refreshCartUI).then(function () {
            return checkoutMode ? getCart().then(verifyCheckoutCart) : applyDomControls();
          });
        }
        return checkoutMode ? verifyCheckoutCart(cart) : applyDomControls(cart);
      })
      .catch(function (error) {
        reportIssue(checkoutMode ? 'checkout-cart-verify-failed' : 'background-reconcile-failed', error);
        return checkoutMode ? blockCheckout('checkout-cart-verify-failed', error) : null;
      });
  }

  // in-flight ガード（二重追加・競合防止）。処理中の更新は 1 回だけ追走。
  function run(cart) {
    if (inFlight) {
      pending = true;
      return inFlightPromise || Promise.resolve();
    }
    inFlight = true;
    inFlightPromise = reconcile({ mode: 'background', cart: cart }).then(finish, finish);
    return inFlightPromise;
    function finish() {
      inFlight = false;
      if (pending) {
        pending = false;
        run();
        return;
      }
      inFlightPromise = null;
    }
  }

  function waitForBackgroundReconcile() {
    if (!inFlightPromise) return Promise.resolve();
    return inFlightPromise.then(waitForBackgroundReconcile, waitForBackgroundReconcile);
  }

  function cartNeedsCheckoutGuard(cart) {
    return isGiftActive() && isCartPayload(cart) && cartHasTarget(cart);
  }

  function updateGuardedCartUI(cart, forceHideDynamicCheckout) {
    var hideDynamicCheckout = !!forceHideDynamicCheckout || cartNeedsCheckoutGuard(cart);
    var dynamicCheckoutBlocks = document.querySelectorAll('.cart__dynamic-checkout-buttons');

    Array.prototype.forEach.call(dynamicCheckoutBlocks, function (block) {
      if (hideDynamicCheckout) {
        if (block.dataset.giftAutoAddHidden !== 'true') {
          block.dataset.giftAutoAddOriginalDisplay = block.style.display || '';
        }
        block.dataset.giftAutoAddHidden = 'true';
        block.style.display = 'none';
        block.setAttribute('aria-hidden', 'true');
        return;
      }

      if (block.dataset.giftAutoAddHidden === 'true') {
        block.style.display = block.dataset.giftAutoAddOriginalDisplay || '';
        block.removeAttribute('aria-hidden');
        delete block.dataset.giftAutoAddHidden;
        delete block.dataset.giftAutoAddOriginalDisplay;
      }
    });
  }

  function clearStuckSyncUI() {
    document.documentElement.classList.remove('gift-auto-add--syncing');

    var messages = document.querySelectorAll('.gift-auto-add__sync-message');
    Array.prototype.forEach.call(messages, function (message) {
      message.remove();
    });

    var disabledCartAreas = document.querySelectorAll('.cart__items--disabled');
    Array.prototype.forEach.call(disabledCartAreas, function (area) {
      area.classList.remove('cart__items--disabled');
    });

    var buttons = document.querySelectorAll('button[name="checkout"], input[name="checkout"]');
    Array.prototype.forEach.call(buttons, function (button) {
      if (button.dataset.giftAutoAddSyncing !== 'true') return;

      if (button.dataset.giftAutoAddSyncOriginalDisabled !== 'true') {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
      }
      button.removeAttribute('aria-busy');
      delete button.dataset.giftAutoAddSyncing;
      delete button.dataset.giftAutoAddSyncOriginalDisabled;
    });
  }

  // ---- プレゼント行のラベル＆ロック（DOM のみ・テンプレート無改変） ----
  // 複合アンカー方式で「プレゼント行だけ」を特定し、同一バリアントの通常購入行を巻き添えにしない。
  //   - Cart Page / Drawer : data-index(= Shopify の line 番号。Dawn 自身が数量/削除で依存) で行を取り、
  //                          data-quantity-variant-id を variant-ID クロスチェックしてからロックする。
  //   - Cart Notification  : 行 id の item.key を完全一致で特定する（index 非依存・最も堅牢）。
  //   - いずれでも特定できなければ何もしない（誤爆 < 不発 の原則）。
  // ここは表示制御専用。追加/削除の判定には用いない（判定は property のみ）。
  function applyDomControls(knownCart) {
    if (!giftVariantId) return Promise.resolve();
    // 直近のカート状態から「プレゼント行の line 番号・key」を求める。
    // 引数があればそれを使い、無ければ最新を取得（再描画後の整合を保証）。
    var source = knownCart ? Promise.resolve(knownCart) : getCart();
    return source
      .then(function (cart) {
        rememberCart(cart);
        updateGuardedCartUI(cart, false);

        if (!cart || !cart.items) return;
        cart.items.forEach(function (item, i) {
          if (!isGiftLine(item)) return;
          lockGiftLine({
            key: item.key,
            line: i + 1, // /cart.js の配列位置(0始まり) + 1 = Shopify の line 番号 = DOM の data-index
          });
        });
      })
      .catch(noop);
  }

  function lockGiftLine(gift) {
    // Cart Page / Drawer : data-index で取得 → variant-ID クロスチェックして確定
    var inputs = document.querySelectorAll('.quantity__input[data-index="' + gift.line + '"]');
    Array.prototype.forEach.call(inputs, function (input) {
      var domVariant = Number(input.getAttribute('data-quantity-variant-id'));
      if (domVariant && domVariant !== giftVariantId) return; // 不一致 → スキップ（巻き添え防止）
      var row = input.closest('.cart-item') || input.closest('tr');
      if (row) lockRow(row, input);
    });

    // Cart Notification : item.key 完全一致（getElementById は特殊文字を含む key もそのまま扱える）
    var notif = gift.key && document.getElementById('cart-notification-product-' + gift.key);
    if (notif) lockRow(notif, notif.querySelector('.quantity__input'));
  }

  function lockRow(row, input) {
    if (!row) return;

    // 数量ロック（input が無いサーフェス＝通知ではスキップ）
    if (input) {
      input.setAttribute('readonly', 'readonly');
      input.setAttribute('aria-disabled', 'true');
      input.style.pointerEvents = 'none';
      var qtyWrap = input.closest('quantity-input') || input.closest('.quantity');
      if (qtyWrap) {
        Array.prototype.forEach.call(qtyWrap.querySelectorAll('.quantity__button'), function (btn) {
          btn.setAttribute('disabled', 'disabled');
          btn.style.pointerEvents = 'none';
          btn.style.opacity = '0.35';
        });
      }
    }

    // 削除ロック
    var removeBtn = row.querySelector('cart-remove-button');
    if (removeBtn) removeBtn.style.display = 'none';

    // ラベル付与（重複防止。再描画でラベルが消えても付け直す）
    if (!row.querySelector('.gift-auto-add__label')) {
      var name = row.querySelector('.cart-item__name');
      var badge = document.createElement('span');
      badge.className = 'gift-auto-add__label';
      badge.textContent = cfg.labelText || 'プレゼント';
      badge.setAttribute(
        'style',
        'display:inline-block;margin:4px 0;padding:2px 8px;border-radius:4px;' +
          'background:#58bf96;color:#fff;font-size:1.1rem;line-height:1.6;font-weight:700;'
      );
      if (name && name.parentNode) {
        name.parentNode.insertBefore(badge, name.nextSibling);
      } else {
        row.insertBefore(badge, row.firstChild);
      }
    }
  }

  // ---- checkout直前検証 ----
  function isCheckoutSubmit(form, submitter) {
    if (!form) return false;
    if (submitter && submitter.getAttribute && submitter.getAttribute('name') === 'checkout') return true;
    return !!form.querySelector('[name="checkout"]');
  }

  function getValidSubmitter(form, submitter) {
    if (
      submitter &&
      submitter.getAttribute &&
      submitter.getAttribute('name') === 'checkout' &&
      submitter.form === form
    ) {
      return submitter;
    }
    return form ? form.querySelector('button[name="checkout"], input[name="checkout"]') : null;
  }

  function setCheckoutLoading(submitter, loading) {
    if (!submitter) return;
    if (loading) {
      submitter.dataset.giftAutoAddOriginalDisabled = submitter.disabled ? 'true' : 'false';
      submitter.dataset.giftAutoAddCheckoutLoading = 'true';
      submitter.disabled = true;
      submitter.setAttribute('aria-disabled', 'true');
      submitter.classList.add('loading');
      return;
    }

    var originalDisabled = submitter.dataset.giftAutoAddOriginalDisabled;
    submitter.classList.remove('loading');
    delete submitter.dataset.giftAutoAddOriginalDisabled;
    delete submitter.dataset.giftAutoAddCheckoutLoading;

    if (originalDisabled !== 'true') {
      submitter.disabled = false;
      submitter.removeAttribute('aria-disabled');
    }
  }

  function showCheckoutError(form) {
    var message = 'カート内容の確認に失敗しました。時間をおいて再度お試しください。';
    var target =
      document.getElementById('cart-errors') ||
      document.getElementById('CartDrawer-CartErrors') ||
      (form && form.parentNode);

    if (!target) return;

    if (target.id === 'cart-errors' || target.id === 'CartDrawer-CartErrors') {
      target.textContent = message;
      return;
    }

    var existing = target.querySelector('.gift-auto-add__checkout-error');
    if (!existing) {
      existing = document.createElement('p');
      existing.className = 'gift-auto-add__checkout-error';
      existing.setAttribute('role', 'alert');
      existing.setAttribute('style', 'color:#d72c0d;margin:8px 0 0;font-size:1.3rem;');
      target.appendChild(existing);
    }
    existing.textContent = message;
  }

  function clearCheckoutError() {
    var errors = document.querySelectorAll('#cart-errors, #CartDrawer-CartErrors, .gift-auto-add__checkout-error');
    Array.prototype.forEach.call(errors, function (el) {
      el.textContent = '';
    });
  }

  function submitCheckout(form, submitter) {
    var validSubmitter = getValidSubmitter(form, submitter);
    var hidden;
    form.dataset.giftAutoAddCheckoutReady = 'true';

    if (!validSubmitter && !form.querySelector('input[name="checkout"][data-gift-auto-add-hidden]')) {
      hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'checkout';
      hidden.value = 'checkout';
      hidden.setAttribute('data-gift-auto-add-hidden', 'true');
      form.appendChild(hidden);
    }

    if (typeof form.requestSubmit === 'function') {
      if (validSubmitter) {
        form.requestSubmit(validSubmitter);
      } else {
        form.requestSubmit();
      }
      return;
    }

    if (!form.querySelector('input[name="checkout"][data-gift-auto-add-hidden]')) {
      hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'checkout';
      hidden.value = 'checkout';
      hidden.setAttribute('data-gift-auto-add-hidden', 'true');
      form.appendChild(hidden);
    }
    HTMLFormElement.prototype.submit.call(form);
  }

  function onCheckoutSubmit(event) {
    var form = event.target;
    var submitter = getValidSubmitter(form, event.submitter || lastCheckoutSubmitter || document.activeElement);

    if (!isCheckoutSubmit(form, submitter)) return;

    if (form.dataset.giftAutoAddCheckoutReady === 'true') {
      delete form.dataset.giftAutoAddCheckoutReady;
      return;
    }

    event.preventDefault();
    if (checkoutInProgress) return;

    checkoutInProgress = true;
    clearCheckoutError();
    setCheckoutLoading(submitter, true);

    Promise.resolve(window.giftWrap ? window.giftWrap.prepareCheckout() : null)
      .then(waitForBackgroundReconcile)
      .then(function () {
        return reconcile({ mode: 'checkout' });
      })
      .then(function (result) {
        // 送信制御はここだけに置き、ラッピング側ではカート確認のみ行う。
        if (result && result.blockCheckout) return result;
        return window.giftWrap ? window.giftWrap.beforeCheckout() : result;
      })
      .then(function (result) {
        if (result && result.blockCheckout) {
          showCheckoutError(form);
          return;
        }

        setCheckoutLoading(submitter, false);
        submitCheckout(form, submitter);
      })
      .catch(function (error) {
        reportIssue('checkout-guard-failed', error);
        showCheckoutError(form);
      })
      .then(function () {
        if (window.giftWrap) window.giftWrap.finishCheckout();
        if (!form.dataset.giftAutoAddCheckoutReady) {
          checkoutInProgress = false;
          setCheckoutLoading(submitter, false);
        }
      });
  }

  function bindCheckoutGuard() {
    document.addEventListener(
      'click',
      function (event) {
        var button = event.target.closest && event.target.closest('button[name="checkout"], input[name="checkout"]');
        if (button) lastCheckoutSubmitter = button;
      },
      true
    );
    document.addEventListener('submit', onCheckoutSubmit, true);
  }

  // ---- 起動 ----
  function boot() {
    clearStuckSyncUI();
    run(); // 初回整合（カートページ再読込時のロック適用・対象の付与/除去）
    bindCheckoutGuard();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event && event.source === GIFT_SOURCE) return; // 自己発火は無視（無限ループ防止）
        run(event && event.cartData);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
